import React, {
	FocusEventHandler,
	forwardRef,
	ForwardRefRenderFunction,
	ReactElement,
	useState
} from 'react'
import { joinClasses, makeClasses } from '@utils/styles'
import { Colors, Constants } from '@utils/css-variables'
import RequiredIcon from '@components/ui/requiredIcon'
import Error from '@components/requestForms/inputs/error.input'
import { IMaskInput } from 'react-imask'
import requestForm from '../request.form'
import * as pageUtils from './_inputs.utils'
import { formatNumberWithSpaces } from '../__forms.utils'

type Props = {
	id: string
	label: string
	value: string
	required?: boolean
	hasError?: boolean
	disabled?: boolean
	type?: 'text' | 'number' | 'date' | 'tel'
	max?: number
	min?: number
	onChange: (selected: string) => void
	classWrapper?: string
	placeholder?: string
	description?: string
	mask?: string
	schema?: RegExp
	errors: string[]
	schemaInvalid?: string
	setError?: React.Dispatch<React.SetStateAction<string[]>>
	displayCurrency?: boolean
	maxCharacters?: number
	isSummaryStep?: boolean
	disableErrorText?: boolean
	scaleToZero?: boolean
}

type Classes = {
	root: string
	input: string
	error: string
	moneyInput: string
	description: string
}

const classes: Classes = makeClasses({
	root: {
		marginBottom: '20px'
	},
	input: {
		fontFamily: Constants.fontStack,
		fontSize: '16px',
		width: '100%',
		background: Colors.white,
		border: `1px solid ${Colors.grey}`,
		fontWeight: 400,
		padding: '8px 12px',
		borderRadius: '0px',
		lineHeight: '26px',
		'&:focus': {
			outline: 'none'
		}
	},
	error: {
		label: {
			color: Colors.errorRed
		},
		input: {
			borderColor: Colors.errorRed,
			'&:focus': {
				borderColor: Colors.errorRed,
				boxShadow: `0 0 0 2px ${Colors.errorRedTransparent20}`
			}
		}
	},
	moneyInput: {
		position: 'relative',
		'& span': {
			position: 'absolute',
			display: 'flex',
			width: '32px',
			height: '44px',
			background: Colors.lightGreyTransparent,
			justifyContent: 'center',
			alignItems: 'center'
		},
		'& input': {
			paddingLeft: '40px'
		},
		'& input::-webkit-outer-spin-button, input::-webkit-inner-spin-button': {
			display: 'none',
			WebkitUserModify: 'read-only !important',
			writingMode: 'inherit !important'
		},
		'& input[type=number]': {
			MozAppearance: 'text-field'
		},
		'& input::-webkit-textfield-decoration-container': {
			width: 'calc(100% - 28px)'
		}
	},
	description: {
		clear: 'both',
		display: 'block',
		marginBottom: '5px',
		padding: '0',
		fontSize: '0.85em',
		lineHeight: '1.25em',
		color: '#999999',
		fontWeight: '400'
	}
})

const TextInputForwardRef: ForwardRefRenderFunction<HTMLInputElement, Props> = (
	{
		id,
		label,
		required,
		hasError,
		disabled,
		value,
		min,
		type = 'text',
		max,
		maxCharacters,
		classWrapper,
		placeholder,
		description,
		onChange,
		schema,
		setError,
		mask,
		errors,
		displayCurrency,
		isSummaryStep = false,
		disableErrorText = false,
		scaleToZero = false
	},
	ref
) => {
	const { pageAssets } = requestForm()

	const [textTooShort, setTextTooShort] = useState<boolean>(false)

	// this is used to determine if the input should be rendered in the summary step
	if (isSummaryStep) {
		if (value) {
			return (
				<>
					<div>
						<strong>{label}</strong>

						<span
							className={joinClasses([
								pageUtils.classes.answerURB,
								'answerURB'
							])}
						>
							{formatNumberWithSpaces(value?.toString())}{' '}
							{displayCurrency && <span>$</span>}
						</span>
					</div>
				</>
			)
		}
		return
	}

	return (
		<div
			className={joinClasses([
				classes.root,
				hasError ? classes.error : '',
				classWrapper || ''
			])}
		>
			<label htmlFor={id}>
				{label} {required && <RequiredIcon />}
				{!disableErrorText && (
					<Error
						hasError={hasError}
						errorText={
							textTooShort
								? !!value
									? pageAssets.label_tooShortErrorText
									: undefined
								: undefined
						}
					/>
				)}
			</label>
			{description && (
				<span className={classes.description}>
					<p>{description}</p>
				</span>
			)}
			<div className={joinClasses([displayCurrency ? classes.moneyInput : ''])}>
				{displayCurrency && <span>$</span>}
				<IMaskInput
					id={id}
					mask={Number}
					scale={scaleToZero ? 0 : 2}
					thousandsSeparator=" "
					{...(!scaleToZero && {
						radix: '.',
						mapToRadix: ['.']
					})}
					value={value?.toString()}
					onAccept={(e) => onChange(e as string)}
					unmask={true} // true|false|'typed'
					inputRef={(el) => {
						if (el) {
							;(el as HTMLInputElement).setSelectionRange(
								el.value.length,
								el.value.length
							)
						}
					}}
					required={required}
					ref={ref}
					//maxLength={maxCharacters}
					max={Number('9'.repeat(maxCharacters ?? 0))}
					// className={
					// 	errors?.includes(Q25ApproximateCostOfWorkInput.name)
					// 		? 'error'
					// 		: ''
					// }
				/>
			</div>
		</div>
	)
}

const MaskedThousandsSeparatorInput = forwardRef(TextInputForwardRef)

export default MaskedThousandsSeparatorInput
